import React, { useState, useRef, useEffect } from 'react';
import { default as EmblaCarousel } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { IconButton } from '@mui/joy';
import { useTheme, useMediaQuery } from '@mui/material';

const Carousel = ({ 
    images = [], 
    width = 'auto',
    height = 'auto',
    aspectRatio = null,
    autoplay = true,
    autoplayInterval = 3000
}) => {
    const [embla, setEmbla] = useState(null);
    const carouselRef = useRef(null);
    const [selectedSlide, setSelectedSlide] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const autoplayPlugin = useRef(Autoplay({ delay: autoplayInterval, stopOnInteraction: false }));

    useEffect(() => {
        if (carouselRef.current) {
            const emblaInstance = EmblaCarousel(carouselRef.current, {
                loop: true,
                align: 'center',
                skipSnaps: false,
                inViewThreshold: 0.7,
            }, [autoplay ? autoplayPlugin.current : null]);
            setEmbla(emblaInstance);
        }
    }, []);

    useEffect(() => {
        if (embla) {
            embla.on('select', () => {
                setSelectedSlide(embla.selectedScrollSnap());
            });
        }
    }, [embla]);

    const scrollPrev = () => {
        if (embla) {
            embla.scrollPrev();
            autoplayPlugin.current.reset();
        }
    };

    const scrollNext = () => {
        if (embla) {
            embla.scrollNext();
            autoplayPlugin.current.reset();
        }
    };

    return (
        <Box
            sx={{
                width: width,
                height: height,
                maxWidth: '100%',
                aspectRatio: aspectRatio || (isMobile ? '16/9' : '16/5'),
                marginX: 2,
                position: 'relative',
                borderRadius: '20px', 
                overflow: 'hidden'
            }}
        >
            <Box
                ref={carouselRef}
                sx={{
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%',
                    height: '100%'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        touchAction: 'pan-y pinch-zoom',
                        height: '100%'
                    }}
                >
                    {images.map((slide, index) => (
                        <Box
                            key={index}
                            sx={{
                                transform: 'translate3d(0, 0, 0)',
                                flex: '0 0 100%',
                                minWidth: 0,
                                position: 'relative',
                                height: '100%',
                            }}
                        >
                            <img
                                src={slide.image}
                                alt={`Слайдер ${index + 1}`}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    userSelect: 'none',
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    zIndex: 100,
                    p: '0.5rem',
                }}
            >
                <IconButton
                    variant="plain"
                    onClick={scrollPrev}
                    sx={{
                        p: 0,
                        m: 0,
                        width: '2.6rem',
                        height: '2.6rem',
                        borderRadius: '50%',
                        boxShadow: 'inset 0 0 0 0.2rem var(--joy-color-detail-medium-contrast)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ":hover": {
                            backgroundColor: "#1b1b1b40"
                        }
                    }}
                >
                    <KeyboardArrowLeft />
                </IconButton>
                
                <IconButton
                    variant="plain"
                    onClick={scrollNext}
                    sx={{
                        p: 0,
                        m: 0,
                        width: '2.6rem',
                        height: '2.6rem',
                        borderRadius: '50%',
                        boxShadow: 'inset 0 0 0 0.2rem var(--joy-color-detail-medium-contrast)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ":hover": {
                            backgroundColor: "#1b1b1b40"
                        }
                    }}
                >
                    <KeyboardArrowRight />
                </IconButton>
            </Box>
            
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    bottom: '1rem',
                    width: '100%', 
                }}
            >
                {images.map((_, index) => (
                    <Button
                        key={index}
                        variant="plain"
                        onClick={() => {
                            if (embla) {
                                embla.scrollTo(index);
                                autoplayPlugin.current.reset();
                            }
                        }}
                        sx={{
                            width: '3.6rem',
                            height: '3.6rem',
                            borderRadius: '50%',
                            boxShadow: `inset 0 0 0 0.2rem ${selectedSlide === index
                                    ? 'var(--joy-color-text-body)'
                                    : 'var(--joy-color-detail-medium-contrast)'
                                }`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:not(:last-child)': {
                                mr: '0.6rem',
                            },
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default Carousel;
